import { Float } from '@headlessui-float/react';
import { Popover } from '@headlessui/react';
import { ArrowDownIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useNavigation } from '@remix-run/react';
import { cn } from '@theprintbar/shared';
import clsx from 'clsx';
import React from 'react';
import { ButtonSize, ButtonVariant, OutlineButton, TransparentLinkButton, defaultButtonClasses } from '../../components';
import { BaseTableSearchQuery, getTableSearchURL } from './baseHelpers';

export interface PaginationProps {
	total?: number | null;
	searchKey?: string;
	searchValue?: string;
	searchParams?: BaseTableSearchQuery;
	itemsPerPageOptions?: number[];
	// When false, controls navigate with JS instead of rendering an <a href>, so crawlers can't discover the URLs
	linkPages?: boolean;
	linkItemsPerPage?: boolean;
}

export const defaultItemsPerPageOptions = [10, 25, 50, 100];

interface PageControlProps {
	to: string;
	link: boolean;
	className?: string;
	disabled?: boolean;
	children: React.ReactNode;
	'aria-current'?: 'page';
}

function PageControl({ to, link, className, disabled, children, ...props }: PageControlProps) {
	const navigate = useNavigate();

	if (link) {
		return (
			<TransparentLinkButton to={to} className={className} disabled={disabled} {...props}>
				{children}
			</TransparentLinkButton>
		);
	}

	// Matches TransparentLinkButton's styling
	return (
		<button
			type="button"
			onClick={() => navigate(to)}
			disabled={disabled}
			className={cn(
				defaultButtonClasses,
				ButtonVariant.transparent,
				ButtonSize.M,
				className,
				disabled && 'text-grey opacity-50 pointer-events-none transition-all duration-500',
			)}
			{...props}
		>
			{children}
		</button>
	);
}

export const Pagination: React.FC<PaginationProps> = ({
	searchParams,
	searchKey,
	searchValue,
	total,
	itemsPerPageOptions,
	linkPages = true,
	linkItemsPerPage = true,
}) => {
	if (!searchParams) {
		return null;
	}

	const optionsPerPage = itemsPerPageOptions || defaultItemsPerPageOptions;
	const itemsPerPage = searchParams.itemsPerPage || optionsPerPage[0];
	const currentPage = searchParams.page || 1;
	const totalItems = total ?? 0;

	const isNavigating = useNavigation().state !== 'idle';
	const navigate = useNavigate();

	if (totalItems <= optionsPerPage[0]) {
		return null;
	}

	const pageCount = Math.ceil(totalItems / itemsPerPage);

	const getItemsPerPageURL = (value: number) =>
		getTableSearchURL({ param: 'itemsPerPage', value, searchKey, searchValue, currentQuery: searchParams });

	const canNext = currentPage < pageCount;
	const canPrev = currentPage > 1;

	const getButtons = () => {
		let buttons: JSX.Element[] = [];

		for (let i = 1; i <= pageCount; i++) {
			const pageButton = (
				<PageControl
					link={linkPages}
					key={`pagination-${i}`}
					to={getTableSearchURL({
						param: 'page',
						value: i,
						searchKey,
						searchValue,
						currentQuery: searchParams,
					})}
					className={clsx(
						i === currentPage ? 'text-blue underline' : 'text-gray-500 hover:underline hover:text-blue hover:border-gray-300',
					)}
					disabled={isNavigating}
					aria-current="page"
				>
					{i}
				</PageControl>
			);

			const divider = (
				<TransparentLinkButton key={`pagination-divider-${i}`} to="#" disabled>
					-
				</TransparentLinkButton>
			);
			if (pageCount > 8) {
				//Always have a divider after #3
				if (i === 1) {
					buttons.push(pageButton);
					if (currentPage > 4) {
						buttons.push(divider);
					}
				} else if (i === pageCount) {
					if (currentPage < pageCount - 3) {
						buttons.push(divider);
					}
					buttons.push(pageButton);
				} else if (
					i === currentPage ||
					i === currentPage - 1 ||
					i === currentPage + 1 ||
					(currentPage < 5 && i < 6) ||
					(currentPage >= pageCount - 3 && i > pageCount - 5)
				) {
					buttons.push(pageButton);
				}
			} else {
				buttons.push(pageButton);
			}
		}

		return buttons;
	};

	return (
		<div className="relative sm:flex items-center border-t border-gray-200 my-4 px-4 h-12">
			<nav className={cn('flex-1 flex items-center justify-center', pageCount === 1 && 'hidden')}>
				<PageControl
					link={linkPages}
					to={getTableSearchURL({
						param: 'page',
						value: Math.min(currentPage - 1, pageCount),
						searchKey,
						searchValue,
						currentQuery: searchParams,
					})}
					className={cn('text-grey hover:text-blue', canPrev && 'hover:underline')}
					disabled={currentPage === 1 || isNavigating}
				>
					Previous
				</PageControl>
				<div className="flex">{getButtons()}</div>
				<PageControl
					link={linkPages}
					to={getTableSearchURL({
						param: 'page',
						value: currentPage + 1,
						searchKey,
						searchValue,
						currentQuery: searchParams,
					})}
					className={cn('text-grey hover:text-blue', canNext && 'hover:underline')}
					disabled={currentPage === pageCount || isNavigating}
				>
					Next
				</PageControl>
			</nav>

			<Popover as="div" className="md:absolute md:right-4 md:top-1/2 md:-translate-y-1/2">
				{({ open }) => (
					<Float
						placement="bottom-end"
						offset={13}
						arrow={5}
						transform={false}
						floatingAs="div"
						enter="transition ease-out duration-100"
						enterFrom="transform opacity-0 scale-95"
						enterTo="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95"
						tailwindcssOriginClass
					>
						<Popover.Button as={OutlineButton} className="border-grey text-grey" size={ButtonSize.XS} icon={ArrowDownIcon}>
							Items per page: {itemsPerPage}
						</Popover.Button>

						<Popover.Panel className="rounded shadow-lg border border-blue bg-white">
							<div className="flex flex-col z-10 text-xs">
								{(itemsPerPageOptions || defaultItemsPerPageOptions).map(value =>
									linkItemsPerPage ? (
										<Popover.Button as={Link} key={value} to={getItemsPerPageURL(value)} className="hover:bg-blue/5 py-2 px-4">
											{value}
										</Popover.Button>
									) : (
										<Popover.Button
											key={value}
											onClick={() => navigate(getItemsPerPageURL(value))}
											className="hover:bg-blue/5 py-2 px-4 text-left"
										>
											{value}
										</Popover.Button>
									),
								)}
							</div>
						</Popover.Panel>
					</Float>
				)}
			</Popover>
		</div>
	);
};

export default Pagination;
